/* ═══════════════════════════════════════════════════════════════
   Home Dashboard — Phase 2
   Landing page with quick stats, active projects, recent items
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { Layers, FolderKanban, LayoutGrid, Lock } from 'lucide-react';
import { SpecService } from '../services/specService';
import { ProjectService } from '../services/projectService';
import { FormaService } from '../services/formaService';
import { sanitizeProjectName } from './projects/helpers';
import EmptyState from '../components/EmptyState';
import { Button } from '../components/ui/button';
import { Icon } from '../components/ui/Icon';
import { rem } from '../lib/rem';
import type { Project, SpecMaster, StandardConfig } from '../../shared/types';

interface HomeProps {
  onNavigate: (view: string) => void;
  toast: (msg: string, isError?: boolean) => void;
}

function Home({ onNavigate, toast }: HomeProps) {
  const [projects, setProjects] = React.useState<Project[]>([]);
  const [specMasters, setSpecMasters] = React.useState<SpecMaster[]>([]);
  const [formas, setFormas] = React.useState<StandardConfig[]>([]);

  React.useEffect(() => {
    loadData();
  }, []);
  React.useEffect(() => {
    const handler = () => loadData();
    window.addEventListener('specforma-sync-complete', handler);
    return () => window.removeEventListener('specforma-sync-complete', handler);
  }, []);

  async function loadData(retry = true) {
    // Use allSettled so one table failing doesn't blank the entire dashboard
    const [pResult, bResult, fResult] = await Promise.allSettled([
      ProjectService.list(),
      SpecService.listMasters(),
      FormaService.list(),
    ]);
    if (pResult.status === 'fulfilled') setProjects(pResult.value);
    else console.warn('Home: projects failed:', pResult.reason);
    if (bResult.status === 'fulfilled') setSpecMasters(bResult.value);
    else console.warn('Home: specMasters failed:', bResult.reason);
    if (fResult.status === 'fulfilled') setFormas(fResult.value || []);
    else console.warn('Home: formas failed:', fResult.reason);

    const anyFailed = [pResult, bResult].some((r) => r.status === 'rejected');
    if (anyFailed && retry) setTimeout(() => loadData(false), 1500);
  }

  function statusBadge(s: string) {
    if (s === 'in-progress')
      return {
        bg: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
        color: 'var(--color-warning)',
        text: 'In Progress',
      };
    if (s === 'planning')
      return {
        bg: 'color-mix(in srgb, var(--color-info) 12%, transparent)',
        color: 'var(--color-info)',
        text: 'Planning',
      };
    if (s === 'complete')
      return { bg: 'rgba(var(--acr),0.12)', color: 'var(--ok)', text: 'Complete' };
    if (s === 'active') return { bg: 'rgba(var(--acr),0.12)', color: 'var(--ok)', text: 'Active' };
    if (s === 'draft')
      return {
        bg: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
        color: 'var(--color-warning)',
        text: 'Draft',
      };
    return { bg: 'var(--sa)', color: 'var(--tm)', text: s || 'Unknown' };
  }

  function timeAgo(d: string) {
    if (!d) return '';
    const ms = Date.now() - new Date(d).getTime();
    const mins = Math.floor(ms / 60000);
    if (mins < 60) return mins + 'm ago';
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return hrs + 'h ago';
    const days = Math.floor(hrs / 24);
    if (days < 30) return days + 'd ago';
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  const activeProjects = projects.filter((p) => p.status !== 'closed').slice(0, 4);
  const recentSpecMasters = [...specMasters]
    .sort((a, b) => (b.lastModified || '').localeCompare(a.lastModified || ''))
    .slice(0, 6);
  const recentFormas = formas.slice(0, 4);

  const cardBase: React.CSSProperties = {
    background: 'var(--sf)',
    borderRadius: 'var(--radius-lg)',
    border: '1px solid var(--bd)',
    overflow: 'hidden',
  };

  return (
    <div style={{ padding: 'var(--space-5)', overflow: 'auto', height: '100%' }}>
      {/* Welcome */}
      <div style={{ marginBottom: 'var(--space-5)' }}>
        <h1
          style={{
            fontSize: 'var(--font-size-3xl)',
            fontWeight: 700,
            margin: '0 0 var(--space-1)',
          }}
        >
          Welcome to SpecForma
        </h1>
        <p style={{ fontSize: 'var(--font-size-md)', color: 'var(--tm)', margin: 0 }}>
          Your specification management dashboard
        </p>
      </div>

      {/* Quick stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-6)',
        }}
      >
        {[
          {
            label: 'Master Specs',
            val: specMasters.length,
            color: 'var(--ac)',
            tint: 'rgba(var(--acr),0.12)',
            nav: 'spec-masters',
            Icon: Layers,
          },
          {
            label: 'Projects',
            val: projects.length,
            color: 'var(--color-info)',
            tint: 'color-mix(in srgb, var(--color-info) 12%, transparent)',
            nav: 'projects',
            Icon: FolderKanban,
          },
          {
            label: 'Master Formas',
            val: formas.length,
            color: 'var(--color-warning)',
            tint: 'color-mix(in srgb, var(--color-warning) 12%, transparent)',
            nav: 'formas',
            Icon: LayoutGrid,
          },
        ].map((s) => (
          <div
            key={s.label}
            onClick={() => onNavigate(s.nav)}
            style={{
              ...cardBase,
              padding: 'var(--space-4)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-3)',
              transition: 'all var(--duration-base) var(--ease-out)',
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
              (e.currentTarget as HTMLElement).style.boxShadow = 'var(--shadow-lg)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLElement).style.transform = '';
              (e.currentTarget as HTMLElement).style.boxShadow = '';
            }}
          >
            <div
              style={{
                width: rem(42),
                height: rem(42),
                borderRadius: 'var(--radius-lg)',
                flexShrink: 0,
                background: s.tint,
                color: s.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Icon icon={s.Icon} em={1.6154} strokeWidth={1.8} aria-hidden="true" />
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 'var(--font-size-3xl)',
                  fontWeight: 700,
                  color: 'var(--tx)',
                  lineHeight: 1,
                }}
              >
                {s.val}
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-base)',
                  color: 'var(--tm)',
                  fontWeight: 600,
                  marginTop: 'var(--space-1)',
                }}
              >
                {s.label}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Active Projects */}
      <div style={{ marginBottom: 'var(--space-5)' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--space-3)',
          }}
        >
          <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, margin: 0 }}>
            Active Projects
          </h2>
          <Button
            variant="outline"
            size="sm"
            className="text-muted"
            onClick={() => onNavigate('projects')}
          >
            View all
          </Button>
        </div>
        {activeProjects.length === 0 ? (
          <EmptyState
            icon={<Icon icon={FolderKanban} em={2.1538} strokeWidth={1.75} aria-hidden="true" />}
            title="No active projects"
          />
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
              gap: 'var(--space-2)',
            }}
          >
            {activeProjects.map((p) => {
              const sb = statusBadge(p.status || 'active');
              return (
                <div
                  key={p.id}
                  onClick={() => onNavigate('projects')}
                  style={{
                    ...cardBase,
                    padding: 'var(--space-4)',
                    cursor: 'pointer',
                    transition: 'all var(--duration-base) var(--ease-out)',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)';
                    (e.currentTarget as HTMLElement).style.boxShadow = 'var(--shadow-md)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.transform = '';
                    (e.currentTarget as HTMLElement).style.boxShadow = '';
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                      marginBottom: 'var(--space-1)',
                    }}
                  >
                    <span style={{ fontSize: 'var(--font-size-md)', fontWeight: 600, flex: 1 }}>
                      {sanitizeProjectName(p.name)}
                    </span>
                    <span
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        padding: `${rem(2)} ${rem(6)}`,
                        borderRadius: 'var(--radius-sm)',
                        background: sb.bg,
                        color: sb.color,
                        fontWeight: 600,
                      }}
                    >
                      {sb.text}
                    </span>
                  </div>
                  <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
                    {p.projectNumber} — {p.clientName}
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--tm)',
                      marginTop: 'var(--space-1)',
                    }}
                  >
                    Updated {timeAgo(p.updatedAt)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Master Specs */}
      <div style={{ marginBottom: 'var(--space-5)' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--space-3)',
          }}
        >
          <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, margin: 0 }}>
            Recent Master Specs
          </h2>
          <Button
            variant="outline"
            size="sm"
            className="text-muted"
            onClick={() => onNavigate('spec-masters')}
          >
            View all
          </Button>
        </div>
        {recentSpecMasters.length === 0 ? (
          <EmptyState
            icon={<Icon icon={Layers} em={2.1538} strokeWidth={1.75} aria-hidden="true" />}
            title="No master specs yet"
          />
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
              gap: 'var(--space-2)',
            }}
          >
            {recentSpecMasters.map((m: any) => {
              const sb = statusBadge(m.status);
              return (
                <div
                  key={m.id}
                  onClick={() => onNavigate('spec-masters')}
                  style={{
                    ...cardBase,
                    padding: 'var(--space-4)',
                    cursor: 'pointer',
                    transition: 'all var(--duration-base) var(--ease-out)',
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)';
                    (e.currentTarget as HTMLElement).style.boxShadow = 'var(--shadow-md)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.transform = '';
                    (e.currentTarget as HTMLElement).style.boxShadow = '';
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--space-2)',
                      marginBottom: 'var(--space-1)',
                    }}
                  >
                    <span
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 'var(--font-size-base)',
                        fontWeight: 700,
                        color: 'var(--ac)',
                      }}
                    >
                      {(m as any).csiNumber}
                    </span>
                    <span
                      style={{
                        fontSize: 'var(--font-size-sm)',
                        padding: `${rem(2)} ${rem(5)}`,
                        borderRadius: 'var(--radius-sm)',
                        background: sb.bg,
                        color: sb.color,
                        fontWeight: 600,
                      }}
                    >
                      {sb.text}
                    </span>
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-base)',
                      fontWeight: 500,
                      marginBottom: 'var(--space-1)',
                    }}
                  >
                    {m.title}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--tm)' }}>
                    {(m.systems || []).length} systems — {timeAgo(m.lastModified)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Formas */}
      <div style={{ marginBottom: 'var(--space-5)' }}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--space-3)',
          }}
        >
          <h2 style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, margin: 0 }}>
            Master Formas
          </h2>
          <Button
            variant="outline"
            size="sm"
            className="text-muted"
            onClick={() => onNavigate('formas')}
          >
            View all
          </Button>
        </div>
        {recentFormas.length === 0 ? (
          <EmptyState
            icon={<Icon icon={LayoutGrid} em={2.1538} strokeWidth={1.75} aria-hidden="true" />}
            title="No master formas yet — create one in the Forma editor"
          />
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
              gap: 'var(--space-2)',
            }}
          >
            {recentFormas.map((b: any) => (
              <div
                key={b.id}
                onClick={() => onNavigate('formas')}
                style={{
                  ...cardBase,
                  padding: 'var(--space-4)',
                  cursor: 'pointer',
                  transition: 'all var(--duration-base) var(--ease-out)',
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)';
                  (e.currentTarget as HTMLElement).style.boxShadow = 'var(--shadow-md)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLElement).style.transform = '';
                  (e.currentTarget as HTMLElement).style.boxShadow = '';
                }}
              >
                <div
                  style={{
                    fontSize: 'var(--font-size-md)',
                    fontWeight: 600,
                    marginBottom: 'var(--space-1)',
                  }}
                >
                  {b.docSettings?.standardName || b.name || b.standardName || 'Untitled forma'}
                </div>
                {b.docSettings?.clientName && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-base)',
                      color: 'var(--tm)',
                      marginBottom: 'var(--space-1)',
                    }}
                  >
                    {b.docSettings.clientName}
                  </div>
                )}
                <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--tm)' }}>
                  Modified {timeAgo(b.lastModified || b.modifiedAt)}
                  {b.modifiedBy ? ' by ' + b.modifiedBy : ''}
                </div>
                {b.locked && (
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 'var(--space-1)',
                      fontSize: 'var(--font-size-sm)',
                      padding: `${rem(2)} ${rem(5)}`,
                      borderRadius: 'var(--radius-sm)',
                      background: 'color-mix(in srgb, var(--color-danger) 10%, transparent)',
                      color: 'var(--color-danger)',
                      fontWeight: 600,
                      marginTop: 'var(--space-1)',
                    }}
                  >
                    <Icon icon={Lock} size="md" strokeWidth={2} aria-hidden="true" />
                    Locked
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Expose as global for legacy compat
(window as any).Home = Home;

export { Home };
